import {Controller} from "@hotwired/stimulus";
import TomSelect from "tom-select";

// DDT-3968: a fast series of clicks must cost one request, not one per click.
const COUNT_DEBOUNCE_MS = 300;
// Past this the count is not worth waiting for; the button falls back to its
// plain label and stays usable.
const COUNT_TIMEOUT_MS = 5000;

// DDT-3968: analytics label per filter, keyed by Stimulus target name. These are
// the query var names each filter writes into the listing URL, so GA, the count
// endpoint and the URL all describe a filter the same way. The three legal-aid
// radios report as one filter, which is what they are.
const FILTER_ANALYTICS_LABELS = {
    legalAidYes: "aid",
    legalAidNo: "aid",
    legalAidMaybe: "aid",
    priceOfFirstAppointmentOrHourlyRate: "tarifs",
    online: "video",
    repliesFast: "replies_fast",
    optinPhone: "optin_phone",
    language: "language",
    feminin: "feminin",
    masculin: "masculin"
};

// DDT-3968: the two autocompletes call filterChanged() from their own selection
// and clear handlers — the filter is the hidden slug they write, not the text
// being typed. Their text input is inside the modal, so its native change event
// on blur would report the same selection twice.
const SELF_TRACKED_TARGETS = ["citySearch", "globalSearch"];

export default class extends Controller {
    static targets = [
        "modal",
        "globalSearch",
        "globalSearchHidden",
        "globalSearchTaxonomyHidden",
        "globalSearchClearButton",
        "citySearch",
        "citySlug",
        "citySearchClearButton",
        "legalAidYes",
        "legalAidNo",
        "legalAidMaybe",
        "legalAidDetails",
        "priceOfFirstAppointmentOrHourlyRate",
        "online",
        "repliesFast",
        "optinPhone",
        "language",
        "feminin",
        "masculin",
        "legalAidDetailsToggleIcon",
        "submitLabel",
        "submitSpinner"
    ];

    static values = {
        lawyersPageUrl: String,
        country: String,
        countLabelZero: String,
        countLabelOne: String,
        countLabelMany: String
    };

    connect() {
        $(this.element).modal({
            backdrop: 'static',
            focus: false,
            show: false,
        });

        // The translated default label, restored whenever a count is
        // unavailable (request failed, timed out, or returned nonsense).
        this.defaultSubmitLabel = this.hasSubmitLabelTarget ? this.submitLabelTarget.textContent.trim() : "";
        this.latestCountRequestId = 0;
        this.lastCount = null;

        this.loadCurrentFilterState();
    }

    disconnect() {
        clearTimeout(this.countDebounceTimeout);
        this.countAbortController?.abort();

        if (this.autoCompleteGlobalSearch) {
            this.autoCompleteGlobalSearch.unInit();
        }

        if (this.autoCompleteCity) {
            this.autoCompleteCity.unInit();
        }

        if (this.tomSelectLanguage) {
            this.tomSelectLanguage.destroy();
        }
    }

    open(event) {
        event.preventDefault();
        event.stopPropagation();

        $(this.element).modal('show');
        document.activeElement?.blur();

        this.loadCurrentFilterState();
        this.initializeAutocomplete();
        this.initializeLanguageDropdown();

        // Show the count for the filters already applied, before anything is touched.
        this.refreshCount();
    }

    loadCurrentFilterState() {
        // Get current search state from localStorage or URL params
        const urlParams = new URLSearchParams(window.location.search);
        let searchState = {};

        try {
            const storedState = localStorage.getItem("search_state");

            if (storedState) {
                searchState = JSON.parse(storedState);
            }
        } catch (e) {
            // Silent fail
        }

        // Load global search (specialism/keywords/s)
        if (this.hasGlobalSearchTarget) {
            this.globalSearchHiddenTarget.value = searchState.specialism || searchState.keywords || searchState.s || urlParams.get("specialism") || urlParams.get("keywords") || urlParams.get("s") || "";

            this.globalSearchTaxonomyHiddenTarget.value = searchState.specialism ? 'specialism' : (searchState.keywords ? 'keywords' : (searchState.s ? 's' : (urlParams.get("specialism") ? 'specialism' : (urlParams.get("keywords") ? 'keywords' : (urlParams.get("s") ? 's' : 'specialism')))));

            if (null !== this.globalSearchTarget.value && "" !== this.globalSearchTarget.value) {
                this.globalSearchClearButtonTarget.classList.remove('d-none');
                this.globalSearchTarget.parentElement?.closest('.filtersSearchForm').classList.add('hide-after');
            }
        }

        // Load city
        if (this.hasCitySearchTarget && this.hasCitySlugTarget) {
            const citySlug = searchState.cities || urlParams.get("cities") || "";

            if (citySlug) {
                // Store the slug in hidden field
                this.citySlugTarget.value = citySlug;
                // Display the formatted name in visible field
                this.citySearchTarget.value = citySlug.charAt(0).toUpperCase() + citySlug.slice(1).replace(/-/g, " ");
            }

            if (null !== this.citySlugTarget.value && "" !== this.citySlugTarget.value) {
                this.citySearchClearButtonTarget.classList.remove('d-none');
                this.citySearchTarget.parentElement?.closest('.filtersSearchForm').classList.add('hide-after');
            }
        }

        // Load legal aid
        if (this.hasLegalAidYesTarget && this.hasLegalAidNoTarget && this.hasLegalAidMaybeTarget) {
            const aidValue = searchState.aid || urlParams.get("aid") || "";
            if (aidValue === "1" || aidValue === true) {
                this.legalAidYesTarget.checked = true;
            } else if (aidValue === "0") {
                this.legalAidNoTarget.checked = true;
            } else {
                this.legalAidMaybeTarget.checked = true;
            }
        }

        // Load tariff checkboxes
        if (this.hasPriceOfFirstAppointmentOrHourlyRateTarget) {
            const priceValue = searchState.tarifs || urlParams.get("tarifs") || "";
            this.priceOfFirstAppointmentOrHourlyRateTarget.checked = priceValue === "1" || priceValue === true;
        }

        // Load availability checkboxes
        if (this.hasOnlineTarget) {
            const videoValue = searchState.video || urlParams.get("video") || "";
            this.onlineTarget.checked = videoValue === "1" || videoValue === true;
        }

        if (this.hasRepliesFastTarget) {
            const repliesFastValue = searchState.replies_fast || urlParams.get("replies_fast") || "";
            this.repliesFastTarget.checked = repliesFastValue === "1" || repliesFastValue === true;
        }

        if (this.hasOptinPhoneTarget) {
            const optinPhoneValue = searchState.optin_phone || urlParams.get("optin_phone") || "";
            this.optinPhoneTarget.checked = optinPhoneValue === "1" || optinPhoneValue === true;
        }

        // Load language
        if (this.hasLanguageTarget) {
            const languageValue = searchState.language || urlParams.get("language") || "";
            this.languageTarget.value = languageValue || "0";
        }

        // Load gender
        if (this.hasFemininTarget) {
            const femininValue = searchState.feminin || urlParams.get("feminin") || "";
            this.femininTarget.checked = femininValue === "1" || femininValue === true;
        }

        if (this.hasMasculinTarget) {
            const masculinValue = searchState.masculin || urlParams.get("masculin") || "";
            this.masculinTarget.checked = masculinValue === "1" || masculinValue === true;
        }
    }

    initializeAutocomplete() {
        const maxGlobalSearchResults = 25;

        // Initialize global search autocomplete
        if (this.hasGlobalSearchTarget && !this.autoCompleteGlobalSearch) {
            let abortControllerSearch = new AbortController();

            this.autoCompleteGlobalSearch = new autoComplete({
                selector: '#search-filters-modal-global-search', data: {
                    src: async (query) => {
                        abortControllerSearch.abort();
                        abortControllerSearch = new AbortController();

                        if ('' === query || 2 > query.length) {
                            try {
                                const source = await fetch(window.jfRestUrl( "justifit/v1/popular-cases" ), {
                                    signal: abortControllerSearch.signal,
                                });
                                return await source.json();
                            } catch (error) {
                                // A newer keystroke aborted this request; never resolve so the
                                // stale response can't overwrite fresh results or break the
                                // autocomplete dropdown (caused sporadic "no results" on mobile).
                                if ('AbortError' === error.name) {
                                    return new Promise(() => {});
                                }

                                return [];
                            }
                        }

                        try {
                            const source = await fetch(window.jfRestUrl( 'justifit/v1/search?' ) + new URLSearchParams({
                                query, max_results: maxGlobalSearchResults,
                            }), {
                                signal: abortControllerSearch.signal,
                            });
                            return await source.json();
                        } catch (error) {
                            // A newer keystroke aborted this request; never resolve so the
                            // stale response can't overwrite fresh results or break the
                            // autocomplete dropdown (caused sporadic "no results" on mobile).
                            if ('AbortError' === error.name) {
                                return new Promise(() => {});
                            }

                            return [];
                        }
                    }, keys: ["name"],
                }, diacritics: true, threshold: 0, resultItem: {
                    highlight: true, element: (item) => {
                        item.insertAdjacentHTML('afterbegin', `
                            <svg class="custom-icon list-search-icon" aria-hidden="true" fill="#AAA">
                                <use xlink:href="#icon-search"></use>
                            </svg>
                        `);
                    },
                }, resultsList: {
                    class: 'global-search-dropdown global-search-dropdown-scroll',
                    maxResults: maxGlobalSearchResults,
                    element: (item) => {
                        let popular_domains_translation = "<?php  _e( 'Recherches populaires', 'ddfr' ); ?>";

                        item.insertAdjacentHTML('afterbegin', `
                            <p class="popular-searches">${popular_domains_translation}</p>`);
                    }
                }, events: {
                    input: {
                        keydown: event => {
                            if (event.key === "Enter") {
                                document.querySelector(".filtersSearchIconBtn").click()
                            }
                        }, selection: (event) => {
                            const selection = event.detail.selection.value;
                            const selectionNameClean = selection.name.split("dans");

                            this.globalSearchTarget.value = selectionNameClean[0].trim();
                            this.globalSearchHiddenTarget.value = event.detail.selection.value.slug;

                            if (selection.taxonomy === 'keywords') {
                                this.globalSearchTaxonomyHiddenTarget.value = 'keywords';
                            } else if (selection.taxonomy === 'lawyer') {
                                this.globalSearchTaxonomyHiddenTarget.value = 's';
                                this.globalSearchHiddenTarget.value = event.detail.selection.value.name;
                            } else if (selection.taxonomy === 'question') {
                                this.globalSearchTaxonomyHiddenTarget.value = 'specialism';

                                if (event.detail.selection.value.parent_domain_slug) {
                                    this.globalSearchHiddenTarget.value = event.detail.selection.value.parent_domain_slug;
                                } else {
                                    this.globalSearchHiddenTarget.value = event.detail.selection.value.slug;
                                }
                            } else {
                                this.globalSearchTaxonomyHiddenTarget.value = 'specialism';

                                if (event.detail.selection.value.parent_domain_slug) {
                                    this.globalSearchHiddenTarget.value = event.detail.selection.value.parent_domain_slug;
                                } else {
                                    this.globalSearchHiddenTarget.value = event.detail.selection.value.slug;
                                }
                            }

                            this.globalSearchClearButtonTarget.classList.remove('d-none');
                            this.globalSearchTarget.parentElement?.closest('.filtersSearchForm').classList.add('hide-after');

                            this.filterChanged(this.globalSearchFilterName());
                        }, focus: () => {
                            if (this.autoCompleteGlobalSearch.input.value) {
                                this.autoCompleteGlobalSearch.start(this.autoCompleteGlobalSearch.input.value);
                            } else {
                                this.autoCompleteGlobalSearch.start("");
                            }
                        }
                    }
                }
            });
        }

        // Initialize city autocomplete
        if (this.hasCitySearchTarget && !this.autoCompleteCity) {
            let abortControllerCity = new AbortController();

            this.autoCompleteCity = new autoComplete({
                selector: '#search-filters-modal-city-search', data: {
                    src: async (query) => {
                        abortControllerCity.abort();
                        abortControllerCity = new AbortController();

                        if ('' === query || 2 > query.length) {
                            try {
                                const source = await fetch(window.jfRestUrl( "justifit/v1/popular-cities" ), {
                                    signal: abortControllerCity.signal,
                                });
                                return await source.json();
                            } catch (error) {
                                // A newer keystroke aborted this request; never resolve so the
                                // stale response can't overwrite fresh results or break the
                                // autocomplete dropdown (caused sporadic "no results" on mobile).
                                if ('AbortError' === error.name) {
                                    return new Promise(() => {});
                                }

                                return [];
                            }
                        }

                        try {
                            return await fetch(window.jfUrls.ajax + "?action=justifit_cities&term=" + query, {
                                signal: abortControllerCity.signal,
                            }).then(function (cities) {
                                return cities.json()
                            }).then(function (cities) {
                                const citiesArray = [];

                                for (let i = 0; i < cities.length; i++) {
                                    citiesArray.push({
                                        name: cities[i].name, slug: cities[i].slug,
                                    });
                                }

                                return citiesArray;
                            });
                        } catch (error) {
                            if ('AbortError' !== error.name) {
                                throw error;
                            }

                            return [];
                        }
                    }, keys: ["name"],
                }, diacritics: true, searchEngine: "loose", threshold: 0, resultItem: {
                    highlight: true, element: (item) => {
                        item.insertAdjacentHTML('afterbegin', `
                            <svg class="custom-icon list-search-icon" aria-hidden="true" fill="#AAA">
                                <use xlink:href="#icon-search"></use>
                            </svg>
                        `);
                    },
                }, resultsList: {
                    class: 'global-city-dropdown global-city-dropdown-scroll',
                    maxResults: 20,
                    element: (list, data) => {
                        let popular_cities_translation = "<?php  _e( 'Villes populaires', 'ddfr' ); ?>";

                        list.insertAdjacentHTML('afterbegin', `
                            <p class="popular-cities">${popular_cities_translation}</p>`);
                        if (!data.results.length) {
                            const message = document.createElement("div");
                            message.setAttribute("class", "no_result");
                            message.innerHTML = ` <svg class="custom-icon list-search-icon" aria-hidden="true" fill="#AAA">
                                <use xlink:href="#icon-search"></use>
                            </svg>Aucun résultat pour "${data.query}"`;
                            list.appendChild(message);
                        }
                    },
                    noResults: true,
                }, events: {
                    input: {
                        selection: (event) => {
                            this.autoCompleteCity.input.value = event.detail.selection.value.name;

                            if (this.hasCitySlugTarget) {
                                this.citySlugTarget.value = event.detail.selection.value.slug;
                            }

                            if (this.hasCitySlugTarget && null !== this.citySlugTarget.value && "" !== this.citySlugTarget.value) {
                                this.citySearchClearButtonTarget.classList.remove('d-none');
                                this.citySearchTarget.parentElement?.closest('.filtersSearchForm').classList.add('hide-after');
                            } else {
                                this.citySearchClearButtonTarget.classList.add('d-none');
                                this.citySearchTarget.parentElement?.closest('.filtersSearchForm').classList.remove('hide-after');
                            }

                            this.filterChanged('cities');
                        }, focus: () => {
                            if (this.autoCompleteCity.input.value) {
                                this.autoCompleteCity.start(this.autoCompleteCity.input.value);
                            } else {
                                this.autoCompleteCity.start("");
                            }
                        }
                    }
                }
            });
        }
    }

    initializeLanguageDropdown() {
        if (this.hasLanguageTarget && !this.tomSelectLanguage) {
            this.tomSelectLanguage = new TomSelect(
                this.languageTarget, {
                    create: false,
                    maxItems: 1,
                    searchField: []
                });
        }
    }

    toggleLegalAidDetails(event) {
        event.preventDefault();

        if (this.legalAidDetailsTarget.classList.contains('d-none')) {
            this.legalAidDetailsTarget.classList.remove('d-none');
            this.legalAidDetailsToggleIconTarget.innerHTML = '<';
        } else {
            this.legalAidDetailsTarget.classList.add('d-none');
            this.legalAidDetailsToggleIconTarget.innerHTML = '>';
        }
    }

    clearCityInput() {
        if (this.hasCitySearchTarget) {
            this.citySearchTarget.value = "";
        }

        if (this.hasCitySlugTarget) {
            this.citySlugTarget.value = "";
        }

        this.citySearchClearButtonTarget.classList.add('d-none');
        this.citySearchTarget.parentElement?.closest('.filtersSearchForm').classList.remove('hide-after');

        this.filterChanged('cities');
    }

    /**
     * The taxonomy the "besoin" field currently searches (specialism, keywords
     * or s), which is also the URL parameter it writes.
     */
    globalSearchFilterName() {
        return this.hasGlobalSearchTaxonomyHiddenTarget
            ? this.globalSearchTaxonomyHiddenTarget.value.trim() || 'specialism'
            : 'specialism';
    }

    clearGlobalSearch(event) {
        event.preventDefault();

        // Read before the fields are cleared, or the filter loses its name.
        const filterName = this.globalSearchFilterName();

        if (this.hasGlobalSearchTarget) {
            this.globalSearchTarget.value = "";
        }

        if (this.hasGlobalSearchHiddenTarget) {
            this.globalSearchHiddenTarget.value = "";
        }

        if (this.hasGlobalSearchTaxonomyHiddenTarget) {
            this.globalSearchTaxonomyHiddenTarget.value = "";
        }

        this.globalSearchClearButtonTarget.classList.add('d-none');
        this.globalSearchTarget.parentElement?.closest('.filtersSearchForm').classList.remove('hide-after');

        this.filterChanged(filterName);
    }

    /**
     * DDT-3968: the filter set as URL parameters, shared by submit() and the
     * count request, so the number in the button always describes the page the
     * button leads to.
     */
    buildFilterParams() {
        const params = new URLSearchParams();

        // Global search
        if (this.hasGlobalSearchTarget && this.globalSearchTarget.value.trim() !== "") {
            params.set(this.globalSearchTaxonomyHiddenTarget.value.trim(), this.globalSearchHiddenTarget.value.trim());
        }

        // City
        if (this.hasCitySlugTarget && this.citySlugTarget.value.trim() !== "") {
            params.set("cities", this.citySlugTarget.value.trim());
        }

        // Legal aid
        if (this.hasLegalAidYesTarget && this.legalAidYesTarget.checked) {
            params.set("aid", "1");
        }

        if (this.hasLegalAidNoTarget && this.legalAidNoTarget.checked) {
            params.set("aid", "0");
        }

        // Tariff
        if (this.hasPriceOfFirstAppointmentOrHourlyRateTarget && this.priceOfFirstAppointmentOrHourlyRateTarget.checked) {
            params.set("tarifs", "1");
        }

        // Availability
        if (this.hasOnlineTarget && this.onlineTarget.checked) {
            params.set("video", "1");
        }

        if (this.hasRepliesFastTarget && this.repliesFastTarget.checked) {
            params.set("replies_fast", "1");
        }

        if (this.hasOptinPhoneTarget && this.optinPhoneTarget.checked) {
            params.set("optin_phone", "1");
        }

        // Language
        const languageValue = this.hasLanguageTarget ? this.languageTarget.value : "";
        if (languageValue && languageValue !== "0") {
            params.set("language", languageValue);
        }

        // Gender
        if (this.hasFemininTarget && this.femininTarget.checked) {
            params.set("feminin", "1");
        }

        if (this.hasMasculinTarget && this.masculinTarget.checked) {
            params.set("masculin", "1");
        }

        return params;
    }

    /**
     * DDT-3968: Stimulus action for any filter input inside the modal. The
     * autocompletes set their hidden fields programmatically, so they call
     * filterChanged() themselves instead of relying on a change event.
     *
     * Their visible text input still emits a native change on blur, which would
     * report the same selection a second time (QA defect 3), so the delegated
     * handler ignores the inputs that track themselves.
     */
    filtersChanged(event) {
        const stimulusTarget = event?.target?.dataset?.searchFiltersModalTarget;

        if (SELF_TRACKED_TARGETS.includes(stimulusTarget)) {
            return;
        }

        this.filterChanged(this.filterNameFromEvent(event));
    }

    filterChanged(filterName) {
        this.trackFilterChange(filterName);

        clearTimeout(this.countDebounceTimeout);
        this.countDebounceTimeout = setTimeout(() => this.refreshCount(), COUNT_DEBOUNCE_MS);
    }

    filterNameFromEvent(event) {
        const stimulusTarget = event?.target?.dataset?.searchFiltersModalTarget;

        return FILTER_ANALYTICS_LABELS[stimulusTarget] || "";
    }

    async refreshCount() {
        if (!this.hasSubmitLabelTarget) {
            return;
        }

        // A slower response must never overwrite a newer one: only the request
        // that is still the latest when it resolves is allowed to render.
        const requestId = ++this.latestCountRequestId;

        this.countAbortController?.abort();
        this.countAbortController = new AbortController();

        const timeoutId = setTimeout(() => this.countAbortController.abort(), COUNT_TIMEOUT_MS);

        this.toggleCountLoading(true);

        try {
            const response = await fetch(window.jfRestUrl( "justifit/v1/lawyers-count?" ) + this.buildFilterParams(), {
                signal: this.countAbortController.signal,
            });

            if (!response.ok) {
                throw new Error(`Count request failed with status ${response.status}`);
            }

            const data = await response.json();

            if (requestId !== this.latestCountRequestId) {
                return;
            }

            this.renderCount(data?.count);
        } catch (error) {
            // A newer request superseded this one; it owns the label now.
            if (requestId !== this.latestCountRequestId) {
                return;
            }

            // Counting is a nicety: on failure the button goes back to its plain
            // label rather than showing a stale or missing number.
            this.resetCountLabel();
        } finally {
            clearTimeout(timeoutId);

            if (requestId === this.latestCountRequestId) {
                this.toggleCountLoading(false);
            }
        }
    }

    renderCount(count) {
        const matches = Number.parseInt(count, 10);

        if (!Number.isFinite(matches) || matches < 0) {
            this.resetCountLabel();

            return;
        }

        let label;

        if (0 === matches) {
            label = this.countLabelZeroValue;
        } else if (1 === matches) {
            label = this.countLabelOneValue;
        } else {
            label = this.countLabelManyValue;
        }

        if (!label) {
            this.resetCountLabel();

            return;
        }

        this.lastCount = matches;
        this.submitLabelTarget.textContent = label.replace("%d", matches);
    }

    resetCountLabel() {
        this.lastCount = null;
        this.submitLabelTarget.textContent = this.defaultSubmitLabel;
    }

    /**
     * The button stays clickable throughout — a user who does not want to wait
     * for the count can always submit.
     */
    toggleCountLoading(isLoading) {
        if (this.hasSubmitSpinnerTarget) {
            this.submitSpinnerTarget.classList.toggle('d-none', !isLoading);
        }

        this.submitLabelTarget.setAttribute('aria-busy', isLoading ? 'true' : 'false');
    }

    trackFilterChange(filterName) {
        dataLayer.push({
            event: "dataLayer.gaEvent",
            gaEvent: {
                category: "Listing Search filters",
                action: "extra_filters_popup_filter_change",
                label: filterName || ''
            }
        });
    }

    submit(event) {
        event.preventDefault();

        // Build URL with all filter parameters
        const url = new URL(this.lawyersPageUrlValue, window.location.origin);

        this.buildFilterParams().forEach((value, key) => {
            url.searchParams.set(key, value);
        });

        dataLayer.push({
            event: "dataLayer.gaEvent",
            gaEvent: {
                category: "Listing Search filters",
                action: "extra_filters_popup_submit",
                // The count the user acted on, so zero-result submits are visible.
                label: null === this.lastCount ? '' : String(this.lastCount)
            }
        });

        // Close popup and redirect
        window.location.href = url.toString();
    }

    openLeadForm(event) {
        event.preventDefault();

        $(this.element).modal('hide');

        // Session cookie, like every other contact_source writer (DDT-4153).
        window.jfCookies.set("contact_source=search_filters; path=/");

        // Get current filter values to pass to lead form
        const domainSelector = document.querySelector("input[type=hidden][name=specialism]");
        const domainId = domainSelector ? domainSelector.dataset.specialismId : null;

        const keywordSelector = document.querySelector("input[type=hidden][name=keywords]");
        const keywordId = keywordSelector ? keywordSelector.dataset.keywordId : null;

        const citySlug = this.hasCitySlugTarget ? this.citySlugTarget.value.trim() : "";

        openLeadBoxzillaModal(
            domainId,
            keywordId,
            citySlug || null,
            "extra_filters",
            "");

        dataLayer.push({
            event: "dataLayer.gaEvent",
            gaEvent: {
                category: "Lead Form B2C",
                action: "leadform_start_extra_filters",
                label: ''
            }
        });
    }
}
